import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Form } from "react-bootstrap";

// Confirmation dialog for the RecipientSummary "Remove N bad" affordance.
// Lists every bad-verdict candidate with a default-checked
// checkbox + the AI's verdict reason inline. Admin can un-check specific
// rows to keep them (handles the "I disagree with one of the AI's calls"
// case without needing a persistent override mechanism on the model).
//
// Props:
//   show       — modal visibility
//   onClose    — fires when admin dismisses
//   onConfirm  — fires with the array of user_ids the admin chose to remove
//   candidates — the bad-verdict subset of the pool (already filtered by
//                the caller)
//   verdicts   — full verdict hash, so each row can render its reason
export default function RemoveBadConfirmDialog({ show, onClose, onConfirm, candidates, verdicts }) {
  // Default: every bad-verdict candidate is checked. Admin un-checks to keep.
  const [keptUnchecked, setKeptUnchecked] = useState(() => new Set());

  // Reset the unchecked set whenever the modal opens — every open is a
  // fresh decision.
  useEffect(() => {
    if (show) setKeptUnchecked(new Set());
  }, [show]);

  const toggle = (id) =>
    setKeptUnchecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toRemove = useMemo(
    () => candidates.filter((c) => !keptUnchecked.has(c.id)).map((c) => c.id),
    [candidates, keptUnchecked],
  );

  const handleConfirm = () => {
    onConfirm(toRemove);
    onClose();
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="h5">Remove {candidates.length} recipients flagged as bad?</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "60vh", overflowY: "auto" }}>
        <p className="small text-muted mb-3">
          The AI flagged these recipients as a bad fit. Un-check any you want to keep on the list.
        </p>
        <div className="d-flex flex-column gap-2">
          {candidates.map((c) => {
            const checked = !keptUnchecked.has(c.id);
            const reason = verdicts[c.id]?.reason;
            return (
              <div key={c.id} className="d-flex align-items-start gap-2 p-2 border rounded">
                <Form.Check
                  type="checkbox"
                  id={`remove-bad-${c.id}`}
                  checked={checked}
                  onChange={() => toggle(c.id)}
                  aria-label={checked ? `Remove ${c.name}` : `Keep ${c.name}`}
                />
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <div className="fw-semibold">{c.name}</div>
                  {(c.title || c.workplace) && (
                    <div className="text-muted small">{[c.title, c.workplace].filter(Boolean).join(" · ")}</div>
                  )}
                  {reason && <div className="small mt-1">&ldquo;{reason}&rdquo;</div>}
                </div>
              </div>
            );
          })}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="danger" onClick={handleConfirm} disabled={toRemove.length === 0}>
          Remove {toRemove.length} selected
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
